/*
 * ANIME CRUSH — SIZE GUIDE MODAL
 *
 * IT OWNS NO STATE. Choosing a size in the guide forwards a click to the matching
 * chip in ac-variant-picker, which already owns availability, the native <select>
 * and therefore the Add to Cart payload. The brief warns against duplicate variant
 * selectors and against interfering with native Shopify behaviour, and the only
 * reliable way to obey that is to have no second source of truth - not to have two
 * that are carefully kept in sync.
 *
 * So a sold-out size is not re-derived here either. The guide renders the state
 * Liquid computed from the real variants, and after a pick it re-reads the picker
 * rather than predicting what the picker will do.
 *
 * ACCESSIBILITY, the parts that are easy to get wrong:
 *   - focus moves into the panel on open and returns to the trigger on close,
 *     because a modal that dumps focus at the top of the document loses a keyboard
 *     user their place entirely.
 *   - Tab is trapped inside the panel while it is open.
 *   - Escape closes. So does the backdrop.
 *   - the page behind is locked WITHOUT a layout jump: the scrollbar width is
 *     measured and compensated, since it is ~15px on Windows and 0 on overlay
 *     scrollbar platforms, and assuming either one shifts the page.
 */
(function () {
  'use strict';

  var openPanel = null;
  var lastTrigger = null;
  var FOCUSABLE = 'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])';

  function scrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }

  function focusables(box) {
    return Array.prototype.slice.call(box.querySelectorAll(FOCUSABLE))
      .filter(function (el) { return el.offsetParent !== null; });
  }

  function open(id, trigger) {
    var panel = document.getElementById(id);
    if (!panel) return;
    lastTrigger = trigger || null;

    var sbw = scrollbarWidth();
    panel.hidden = false;
    /* Set before locking the body so the compensation and the lock land in the
       same frame and the page cannot shift between them. */
    document.body.style.paddingRight = sbw > 0 ? sbw + 'px' : '';
    document.body.classList.add('ac-size-guide-open');
    openPanel = panel;

    /* Prefer the selected size, so a keyboard user lands on the thing they most
       likely came to change rather than on the close button. */
    var first = panel.querySelector('.AcSizeGuide__Size.is-selected') ||
                focusables(panel)[0];
    if (first) first.focus();
  }

  function close() {
    if (!openPanel) return;
    openPanel.hidden = true;
    document.body.classList.remove('ac-size-guide-open');
    document.body.style.paddingRight = '';
    var t = lastTrigger;
    openPanel = null;
    lastTrigger = null;
    if (t && typeof t.focus === 'function') t.focus();
  }

  /* Re-read the picker and mirror it. Never the other way round. */
  function syncFromPicker(panel) {
    var picker = document.querySelector('[data-ac-variant-picker], .AcPicker');
    if (!picker) return;
    Array.prototype.forEach.call(panel.querySelectorAll('[data-ac-size-guide-pick]'), function (btn) {
      var value = btn.getAttribute('data-ac-size-guide-pick');
      var pos = btn.getAttribute('data-ac-size-guide-position');
      var group = picker.querySelector('[data-ac-option="' + pos + '"]');
      if (!group) return;
      var chip = group.querySelector('[data-ac-value="' + cssEscape(value) + '"]');
      if (!chip) return;
      var selected = chip.classList.contains('is-selected') ||
                     chip.getAttribute('aria-checked') === 'true';
      btn.classList.toggle('is-selected', selected);
      btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
      /* The picker marks an unavailable combination itself; mirror whatever it
         decided rather than recomputing it. */
      var unavailable = chip.classList.contains('is-unavailable') ||
                        chip.hasAttribute('aria-disabled') || chip.disabled;
      btn.classList.toggle('is-soldout', unavailable);
    });
  }

  /* Values here are plain size labels, but an attribute selector still has to be
     escaped - CSS.escape is not in every browser this theme supports. */
  function cssEscape(v) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(v);
    return String(v).replace(/["\\]/g, '\\$&');
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest ? e.target.closest('[data-ac-size-guide-open]') : null;
    if (opener) {
      e.preventDefault();
      open(opener.getAttribute('data-ac-size-guide-open'), opener);
      return;
    }

    if (e.target.closest && e.target.closest('[data-ac-size-guide-close]')) {
      e.preventDefault();
      close();
      return;
    }

    var pick = e.target.closest ? e.target.closest('[data-ac-size-guide-pick]') : null;
    if (pick && openPanel) {
      e.preventDefault();
      /* A sold-out size is still clickable in the picker - Shopify shows the
         unavailable state rather than hiding it - so forward it and let the
         picker decide. Blocking it here would be a second opinion. */
      var value = pick.getAttribute('data-ac-size-guide-pick');
      var pos = pick.getAttribute('data-ac-size-guide-position');
      var picker = document.querySelector('[data-ac-variant-picker], .AcPicker');
      var group = picker && picker.querySelector('[data-ac-option="' + pos + '"]');
      var chip = group && group.querySelector('[data-ac-value="' + cssEscape(value) + '"]');
      if (chip) {
        chip.click();
        /* Let the picker finish, then mirror it. */
        window.setTimeout(function () {
          if (openPanel) syncFromPicker(openPanel);
        }, 60);
      }
    }
  });

  document.addEventListener('keydown', function (e) {
    if (!openPanel) return;
    if (e.key === 'Escape' || e.keyCode === 27) { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab' && e.keyCode !== 9) return;
    var f = focusables(openPanel);
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* The picker can change from outside the guide - the page's own chips, or a
     variant change from the URL - so mirror on any picker click too. */
  document.addEventListener('click', function (e) {
    if (!openPanel) return;
    if (e.target.closest && e.target.closest('[data-ac-value]')) {
      window.setTimeout(function () { if (openPanel) syncFromPicker(openPanel); }, 60);
    }
  }, true);
})();
